import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { validateEmail } from '../validation/authSchemas';
import { useFormValidation } from '../hooks/useFormValidation';
import { authService } from '../services/authService';

interface EmailCheckFormProps {
  onEmailExists: (email: string) => void;
  onEmailNew: (email: string) => void;
  createAccountEnabled?: boolean;
  isLoadingFeatureFlags?: boolean;
}

export const EmailCheckForm: React.FC<EmailCheckFormProps> = ({
  onEmailExists,
  onEmailNew,
  createAccountEnabled = true,
  isLoadingFeatureFlags = false,
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { errors, validateField, clearError, hasErrors } = useFormValidation();

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    setApiError(null);
    
    if (value.trim()) {
      validateField('email', value, validateEmail);
    } else {
      clearError('email');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (hasErrors || !email.trim() || isLoadingFeatureFlags) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await authService.checkEmail(email);
      
      if (response.data.exists) {
        onEmailExists(email);
      } else {
        if (createAccountEnabled) {
          onEmailNew(email);
        } else {
          setApiError('Create Account is currently unavailable. Please sign in with an existing account.');
        }
      }
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Failed to check email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
        }}
      >
        <Box
          component="img"
          src="/logos/logo-wide.png"
          alt="Careva"
          sx={{
            height: 60,
            width: 'auto',
            maxWidth: '100%',
            mb: 3,
            mx: 'auto',
            display: 'block',
          }}
        />
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Welcome to Careva
        </Typography>
        
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Enter your email to continue
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={!!errors.email}
            helperText={errors.email}
            disabled={isLoading}
            sx={{ mb: 3 }}
            autoComplete="email"
            autoFocus
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || hasErrors || !email.trim() || isLoadingFeatureFlags}
            sx={{ mb: createAccountEnabled ? 1.5 : 2 }}
            data-testid="continue-button"
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Continue'
            )}
          </Button>

          {isLoadingFeatureFlags ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : createAccountEnabled ? (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              disabled={isLoading || hasErrors || !email.trim()}
              onClick={() => onEmailNew(email)}
              sx={{ mb: 2 }}
              data-testid="create-account-direct-button"
            >
              Create Account
            </Button>
          ) : null}

          <Typography variant="body2" color="text.secondary" align="center">
            We'll check if you have an account or help you create one
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};
